import Microgame from '../engine/Microgame.js';

export default class RipGame extends Microgame {

    static getPlayback() {
        return {
            title: { fr: `Déchire !`, en: `Rip!`, es: `¡Arranca!` },
            controls: { fr: `Glisse très vite ton doigt d'un bord à l'autre`, en: `Swipe very fast across the object`, es: `Desliza muy rápido de un lado a otro` },
            objective: { fr: `Arrache le bout de scotch d'un coup sec`, en: `Rip off the tape quickly`, es: `Arranca la cinta de un tirón seco` },
            icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="var(--secondary-color)" stroke-width="2"><path d="M21 8H3l3 8h12Z"/><path d="m4 12 16-4"/></svg>`,
            actions: [
                { type: 'wait', duration: 260 },
                { type: 'swipe', fromX: 16, fromY: 32, toX: 20, toY: 66, duration: 520 },
                { type: 'wait', duration: 600 },
            ],
            loopDuration: 2600
        };
    }
    constructor(engine) {
        super(engine);
        this.pages = [];
        this.activePage = null;
        this.startY = 0;
        this.isVictoryAnimating = false;
        this._frozenTimerValue = 0;

        // Generate 16 unique random numbers between 1 and 31
        if (this.isPlayback) {
            this.dates = Array.from({ length: 16 }, (_, i) => i + 1);
            this.targetDate = 1;
        } else {
            const pool = Array.from({length: 31}, (_, i) => i + 1);
            const selected = [];
            for (let i = 0; i < 16; i++) {
                const idx = Math.floor(Math.random() * pool.length);
                selected.push(pool.splice(idx, 1)[0]);
            }
            this.dates = selected.sort((a, b) => a - b);
            this.targetDate = selected[Math.floor(Math.random() * selected.length)];
        }

        const speedMult = engine.speedMultiplier || 1.0;
        // 4s at x1, ~1.08s at x4 (reduced by 100ms per request)
        const adjustedSpeed = 1 + (speedMult - 1) * 0.9; 
        this.duration = 4000 * (speedMult / adjustedSpeed);
    }

    setup() {
        const getSuffix = (n) => {
            if (n > 3 && n < 21) return 'th';
            switch (n % 10) {
                case 1:  return "st";
                case 2:  return "nd";
                case 3:  return "rd";
                default: return "th";
            }
        };
        this.engine.ui.instruction.textContent = `RIP THE ${this.targetDate}${getSuffix(this.targetDate)}!`;

        const grid = document.createElement('div');
        grid.style.display = 'grid';
        grid.style.gridTemplateColumns = 'repeat(4, 1fr)';
        grid.style.gridGap = '10px';
        grid.style.width = '95%';
        grid.style.position = 'absolute';
        grid.style.top = '50%';
        grid.style.left = '50%';
        grid.style.transform = 'translate(-50%, -50%)';
        this.container.appendChild(grid);

        this.dates.forEach((date, index) => {
            const isTarget = date === this.targetDate;
            const pageContainer = document.createElement('div');
            pageContainer.style.position = 'relative';
            pageContainer.style.height = '85px';
            pageContainer.style.background = '#222';
            pageContainer.style.borderRadius = '5px';
            pageContainer.style.borderTop = `4px solid ${this.secondaryColor}`;
            pageContainer.style.boxShadow = 'inset 0 0 10px rgba(0,0,0,0.5)';
            grid.appendChild(pageContainer);

            const page = document.createElement('div');
            page.style.position = 'absolute';
            page.style.width = '100%';
            page.style.height = '100%';
            
            // Highlight target page with different color
            if (isTarget) {
                page.style.background = this.secondaryColor;
                page.style.color = this.primaryColor;
                pageContainer.style.borderTopColor = this.primaryColor;
                page.style.boxShadow = `0 0 15px ${this.secondaryColor}`;
            } else {
                page.style.background = this.primaryColor;
                page.style.color = '#000';
                page.style.boxShadow = `0 4px 10px rgba(0,0,0,0.4)`;
            }

            page.style.borderRadius = '2px 2px 5px 5px';
            page.style.display = 'flex';
            page.style.justifyContent = 'center';
            page.style.alignItems = 'center';
            page.style.fontSize = '28px';
            page.style.fontWeight = '900';
            page.textContent = date;
            page.style.transformOrigin = 'top center';
            page.style.cursor = 'grab';
            page.style.touchAction = 'none';
            page.dataset.date = date;

            pageContainer.appendChild(page);
            this.pages.push(page);

            page.addEventListener('pointerdown', (e) => this.onDown(e, page));
        });

        this.onMove = this.onMove.bind(this);
        this.onUp = this.onUp.bind(this);
        this.engine.input.element.addEventListener('pointermove', this.onMove);
        this.engine.input.element.addEventListener('pointerup', this.onUp);
    }

    update(dt) {
        if (this.isVictoryAnimating) {
            this.engine.timer = this._frozenTimerValue;
            return;
        }
    }

    onDown(e, page) {
        if (this.won || this.lost || this.activePage || this.isVictoryAnimating) return;
        this.activePage = page;
        this.startY = e.clientY;
        page.style.transition = 'none';
        page.style.zIndex = '100';
        page.style.cursor = 'grabbing';
    }

    onMove(e) {
        if (!this.activePage || this.won || this.lost) return;

        const deltaY = e.clientY - this.startY;
        if (deltaY > 0) {
            const rotation = deltaY * 0.2;
            this.activePage.style.transform = `translateY(${deltaY}px) rotate(${rotation}deg)`;

            // Rip threshold
            if (deltaY > 90) {
                const date = parseInt(this.activePage.dataset.date);
                if (date === this.targetDate) {
                    this.handleWin();
                } else {
                    this.handleLose();
                }
            }
        }
    }

    handleWin() {
        this.won = true;
        this.isVictoryAnimating = true;
        this._frozenTimerValue = this.engine.timer;

        this.activePage.style.transition = 'all 0.5s cubic-bezier(0.55, 0.055, 0.675, 0.19)';
        this.activePage.style.transform = 'translateY(800px) rotate(45deg) scale(0.5)';
        this.activePage.style.opacity = '0';

        setTimeout(() => {
            this.isVictoryAnimating = false;
            this.win();
        }, 700);
    }

    handleLose() {
        this.lost = true;
        this.lose();

        // Feedbak for error
        this.activePage.style.transition = 'all 0.2s';
        this.activePage.style.background = '#ff4444';
        this.activePage.style.transform = 'translateY(20px) rotate(5deg)';
    }

    onUp() {
        if (!this.activePage || this.won || this.lost) return;
        this.activePage.style.transition = 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)';
        this.activePage.style.transform = 'none';
        this.activePage.style.zIndex = '';
        this.activePage = null;
    }

    cleanup() {
        super.cleanup();
        this.engine.input.element.removeEventListener('pointermove', this.onMove);
        this.engine.input.element.removeEventListener('pointerup', this.onUp);
    }
}
